import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  FolderZip as ZipIcon
} from '@mui/icons-material';
import { orderService } from '../services/orderService';

function FileDropZone({ onOrderCreated, disabled = false }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const extractPdfsFromZip = async (zipFile) => {
    return new Promise((resolve, reject) => {
      const JSZip = require('jszip');
      
      JSZip.loadAsync(zipFile).then((zip) => {
        const pdfPromises = [];
        const pdfInfos = [];

        zip.forEach((relativePath, zipEntry) => {
          if (relativePath.toLowerCase().endsWith('.pdf')) {
            pdfInfos.push({
              name: relativePath,
              size: zipEntry._data?.uncompressedSize || 0
            });
            pdfPromises.push(
              zipEntry.async('blob').then(blob => ({
                name: relativePath,
                blob: new File([blob], relativePath, { type: 'application/pdf' }),
                size: blob.size
              }))
            );
          }
        });

        if (pdfPromises.length === 0) {
          reject(new Error('No se encontraron archivos PDF en el ZIP'));
          return;
        }

        Promise.all(pdfPromises).then(resolve).catch(reject);
      }).catch(reject);
    });
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setError(null);
    setUploading(true);

    try {
      if (file.type === 'application/pdf') {
        // Archivo PDF individual
        setPreviewFile(file);
        setPdfFiles([{
          name: file.name,
          blob: file,
          size: file.size
        }]);
      } else if (file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
        // Archivo ZIP con múltiples PDFs
        const pdfs = await extractPdfsFromZip(file);
        setPreviewFile(file);
        setPdfFiles(pdfs);
      }
      
      setShowPreview(true);
    } catch (err) {
      setError(err.message || 'Error al procesar el archivo');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleAcceptFile = async () => {
    if (!previewFile || pdfFiles.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      if (pdfFiles.length === 1) {
        // Un solo PDF
        const result = await orderService.createOrder(pdfFiles[0].blob);
        
        if (result.success) {
          if (onOrderCreated) {
            onOrderCreated(result.data);
          }
          handleCancelPreview();
        } else {
          setError(result.message);
        }
      } else {
        // Múltiples PDFs desde ZIP
        const results = [];
        for (const pdf of pdfFiles) {
          const result = await orderService.createOrder(pdf.blob);
          if (result.success) {
            results.push(result.data);
          } else {
            setError(`Error creando pedido para ${pdf.name}: ${result.message}`);
            return;
          }
        }
        
        if (onOrderCreated) {
          // Notificar todos los pedidos creados
          results.forEach(order => onOrderCreated(order));
        }
        handleCancelPreview();
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Error al crear los pedidos');
    } finally {
      setUploading(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewFile(null);
    setPdfFiles([]);
    setShowPreview(false);
    setError(null);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip']
    },
    maxFiles: 1,
    disabled: disabled || uploading || showPreview
  });

  const getDropzoneContent = () => {
    if (uploading && !showPreview) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Procesando archivo...
          </Typography>
        </Box>
      );
    }

    if (isDragReject) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
          <Typography variant="body2" color="error">
            Tipo de archivo no válido. Solo se permiten archivos PDF y ZIP.
          </Typography>
        </Box>
      );
    }

    if (isDragActive) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <UploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="body2" color="primary">
            Suelta el archivo aquí...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <FileIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        <Typography variant="h6" align="center">
          Arrastra y suelta una factura aquí
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          o haz clic para seleccionar un archivo PDF o ZIP
        </Typography>
        <Button variant="outlined" component="span">
          Seleccionar Archivo
        </Button>
      </Box>
    );
  };

  return (
    <Box>
      {/* Zona de drop */}
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: !uploading && !disabled && !showPreview ? 'pointer' : 'default',
          border: '2px dashed',
          borderColor: isDragActive 
            ? 'primary.main' 
            : isDragReject 
              ? 'error.main' 
              : 'grey.300',
          backgroundColor: isDragActive 
            ? 'action.hover' 
            : disabled || showPreview
              ? 'action.disabledBackground'
              : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': !uploading && !disabled && !showPreview ? {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          } : {}
        }}
      >
        <input {...getInputProps()} />
        {getDropzoneContent()}
      </Paper>

      {/* Mostrar errores */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Información adicional */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Tipos de archivo soportados: PDF, ZIP con PDFs. Tamaño máximo: 10MB.
      </Typography>

      {/* Modal de preview */}
      <Dialog
        open={showPreview}
        onClose={handleCancelPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {pdfFiles.length === 1 ? 'Confirmar Creación de Pedido' : `Confirmar Creación de ${pdfFiles.length} Pedidos`}
            <IconButton onClick={handleCancelPreview} disabled={uploading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {previewFile && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Archivo:</strong> {previewFile.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Tamaño:</strong> {(previewFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="body2">
                  <strong>Tipo:</strong> {previewFile.type === 'application/pdf' ? 'PDF individual' : 'ZIP con múltiples PDFs'}
                </Typography>
              </Alert>

              {pdfFiles.length === 1 ? (
                // Vista previa de PDF individual
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PdfIcon color="error" />
                    Vista Previa del PDF
                  </Typography>
                  
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50', mb: 2 }}>
                    <embed
                      src={URL.createObjectURL(pdfFiles[0].blob)}
                      type="application/pdf"
                      width="100%"
                      height="400px"
                      style={{ border: 'none' }}
                    />
                  </Paper>
                </Box>
              ) : (
                // Lista de PDFs en ZIP
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ZipIcon color="primary" />
                    PDFs encontrados en el ZIP
                  </Typography>
                  
                  <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                    <List>
                      {pdfFiles.map((pdf, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemIcon>
                              <PdfIcon color="error" />
                            </ListItemIcon>
                            <ListItemText
                              primary={pdf.name}
                              secondary={`${(pdf.size / 1024 / 1024).toFixed(2)} MB`}
                            />
                          </ListItem>
                          {index < pdfFiles.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                </Box>
              )}

              <Alert severity="warning">
                {pdfFiles.length === 1 
                  ? 'Al confirmar se creará un nuevo pedido en estado "CREATED". Deberás asignar el proveedor y completar los detalles en el siguiente paso.'
                  : `Al confirmar se crearán ${pdfFiles.length} pedidos nuevos en estado "CREATED". Deberás asignar proveedores y completar detalles para cada uno.`
                }
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleCancelPreview} 
            disabled={uploading}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleAcceptFile} 
            disabled={uploading}
            variant="contained"
            startIcon={uploading && <CircularProgress size={20} />}
          >
            {uploading ? 'Creando...' : (pdfFiles.length === 1 ? 'Aceptar y Crear Pedido' : `Crear ${pdfFiles.length} Pedidos`)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FileDropZone; 